import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Category } from '@vendasta/google-analytics';
import { GaGoalData } from '@vendasta/uikit';
import { differenceInDays, format, parse } from 'date-fns';
import { Observable, from } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { alignLocalDateToUTC, alignUTC } from '../../core/metrics.helper';
import {
  Alignment,
  Calendar,
  CompositeFilter_Operator,
  FieldFilter_Operator,
  Operator,
  Order,
  Type,
} from '../../core/multi_location_analytics_sdk/_internal/enums';
import {
  Aggregate,
  AlignmentPeriod,
  DateRange,
  Dimension,
  GroupBy,
  Measure,
  MetricResult,
  OrderBy,
  QueryMetricsRequest,
  QueryMetricsResponse,
} from '../../core/multi_location_analytics_sdk/_internal/objects';
import { MultiLocationAnalyticsService } from '../../core/multi_location_analytics_sdk/multi-location-analytics.service';
import { AnalyticsGraph, AnalyticsGraphDefinition, AnalyticsJSON } from '../analytics/analytics.interfaces';
import { ChartEntry } from '../metrics';
import { COLLECTION_DAYS_THRESHOLD } from './ga-metrics-config';
import { GaGoalMap, GaRecordMap, GaSourceMap } from './ga-metrics.interface';

declare const gapi: { analytics: any };

@Injectable({
  providedIn: 'root',
})
export class GaMetricsService {
  legacyDefaultViewId = '';
  siteId: string;
  accessToken: string;
  lineDimension: string;
  lineMetrics: string;
  respdata: Observable<any>;
  retdata: GaRecordMap;
  defaultViewId: string;

  areaGraphDefinitions: AnalyticsGraphDefinition = {
    dimension: 'ga:date',
    type: 'line',
    graphs: [
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.TOTAL_VISITORS',
        metrics: ['ga:users'],
        format: 'number',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.PAGE_VIEWS',
        metrics: ['ga:pageviews'],
        format: 'number',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.PAGES_SESSION',
        metrics: ['ga:pageviewsPerSession'],
        format: 'number',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.BOUNCE_RATE',
        metrics: ['ga:bounceRate'],
        format: 'percent',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.AVERAGE_TIME_ON_WEBSITE',
        metrics: ['ga:avgSessionDuration'],
        format: 'time',
        type: 'line',
      },
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.PERCENT_NEW_VISITS',
        metrics: ['ga:sessions'],
        format: 'percent',
        type: 'line',
      },
    ],
  };
  pieGraphDefinition: AnalyticsGraphDefinition = {
    dimension: 'ga:channelGrouping',
    type: 'pie',
    filters: 'ga:channelGrouping!=/',
    sort: '-ga:sessions',
    maxResults: '4',
    graphs: [
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.TRAFFIC_SOURCE',
        metrics: ['ga:sessions'],
        format: 'number',
        type: 'pie',
        filters: 'ga:channelGrouping!=/',
        sort: '-ga:sessions',
        maxResults: '4',
      },
    ],
  };
  barGraphDefinition: AnalyticsGraphDefinition = {
    dimension: 'ga:source',
    type: 'bar',
    filters: 'ga:source!=/;ga:source!=(direct)',
    sort: '-ga:sessions',
    maxResults: '4',
    graphs: [
      {
        statName: 'ANALYTICS.ANALYTICS_PANEL.LABELS.TOP_REFERRAL_SOURCES',
        metrics: ['ga:sessions'],
        format: 'number',
        type: 'bar',
        filters: 'ga:source!=/;ga:source!=(direct)',
        sort: '-ga:sessions',
        maxResults: '4',
      },
    ],
  };
  data: GaRecordMap;

  constructor(private multiLocationAnalyticsService: MultiLocationAnalyticsService, private http: HttpClient) {}

  getGaRecords(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
    siteId: string,
    defaultTracking: boolean,
    accessToken: string,
    viewId: string,
  ): Observable<GaRecordMap> {
    this.siteId = siteId;
    if (defaultTracking === false) {
      const req: QueryMetricsRequest = this.getBaseQuery('google_analytics_v2_record', partnerId, businessId);
      // The order of listing each measure in the request matters - the results will be in a list and each of them is in the same order as that in the request
      req.measures = [
        new Measure({
          aggregate: new Aggregate({
            measure: 'total_visitors',
            aggOp: Operator.SUM,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'page_views',
            aggOp: Operator.SUM,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'pages_per_session',
            aggOp: Operator.AVG,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'bounce_rate',
            aggOp: Operator.AVG,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'average_seconds_on_website',
            aggOp: Operator.AVG,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'total_visits',
            aggOp: Operator.SUM,
          }),
        }),
      ];
      req.groupBy = new GroupBy({
        dimension: [
          new Dimension({
            dimension: 'collection_timestamp',
          }),
        ],
      });
      req.alignmentPeriod = new AlignmentPeriod({
        calendar:
          differenceInDays(endDate, startDate) >= COLLECTION_DAYS_THRESHOLD
            ? Calendar.CALENDAR_MONTH
            : Calendar.CALENDAR_DAY,
      });
      req.alignment = Alignment.ALIGN_DELTA;
      req.dateRange = new DateRange({
        start: startDate,
        end: endDate,
      });
      return this.multiLocationAnalyticsService.queryMetrics(req).pipe(
        filter((resp) => resp != null),
        map((resp: QueryMetricsResponse) => {
          const metrics = resp.metricResults[0].metrics.metrics;
          if (!metrics) {
            return {};
          }
          const recordDataForTotalVisitors: ChartEntry[] = [];
          const recordDataForPageViews: ChartEntry[] = [];
          const recordDataForPagesPerSession: ChartEntry[] = [];
          const recordDataForBounceRate: ChartEntry[] = [];
          const recordDataForAvgSecondsOnWeb: ChartEntry[] = [];
          const recordDataForTotalVisits: ChartEntry[] = [];

          metrics.map((m) => {
            // The order aligns with the measurement order in the request
            recordDataForTotalVisitors.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[0],
            } as ChartEntry);
            recordDataForPageViews.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[1],
            } as ChartEntry);
            recordDataForPagesPerSession.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[2],
            } as ChartEntry);
            recordDataForBounceRate.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[3],
            } as ChartEntry);
            recordDataForAvgSecondsOnWeb.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[4],
            } as ChartEntry);
            recordDataForTotalVisits.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[5],
            } as ChartEntry);
          });
          const data = {
            totalVisitors: recordDataForTotalVisitors,
            pageViews: recordDataForPageViews,
            pagesPerSession: recordDataForPagesPerSession,
            bounceRate: recordDataForBounceRate,
            averageSecondsOnWebsite: recordDataForAvgSecondsOnWeb,
            totalVisits: recordDataForTotalVisits,
          } as GaRecordMap;
          return data;
        }),
        shareReplay(1),
      );
    } else {
      return new Observable<GaRecordMap>((observer) => {
        gapi.analytics.ready(function (): void {
          gapi.analytics.auth.authorize({
            serverAuth: {
              access_token: accessToken,
            },
          });
        });
        this.defaultViewId = viewId;
        this.lineDimension = this.areaGraphDefinitions.dimension;
        this.lineMetrics = this.getLineMetrics(this.areaGraphDefinitions);

        const respdata = this.buildQuery(
          this.getQueryOptions(this.lineMetrics, this.lineDimension, this.areaGraphDefinitions, startDate, endDate),
        );
        respdata.subscribe((response) => {
          if (typeof response.rows === 'undefined') {
            observer.next({});
            observer.complete();
            return {};
          }
          const metrics = response.rows;
          if (!metrics) {
            return {};
          }
          const recordDataForTotalVisitors: ChartEntry[] = [];
          const recordDataForPageViews: ChartEntry[] = [];
          const recordDataForPagesPerSession: ChartEntry[] = [];
          const recordDataForBounceRate: ChartEntry[] = [];
          const recordDataForAvgSecondsOnWeb: ChartEntry[] = [];
          const recordDataForTotalVisits: ChartEntry[] = [];

          metrics.map((m) => {
            // The order aligns with the measurement order in the request
            recordDataForTotalVisitors.push({
              name: alignUTC(parse(m[0], 'yyyyMMdd', new Date()).toString()),
              value: Number(m[1]),
            } as ChartEntry);
            recordDataForPageViews.push({
              name: alignUTC(parse(m[0], 'yyyyMMdd', new Date()).toString()),
              value: Number(m[2]),
            } as ChartEntry);
            recordDataForPagesPerSession.push({
              name: alignUTC(parse(m[0], 'yyyyMMdd', new Date()).toString()),
              value: Number(m[3]),
            } as ChartEntry);
            recordDataForBounceRate.push({
              name: alignUTC(parse(m[0], 'yyyyMMdd', new Date()).toString()),
              value: Number(m[4]),
            } as ChartEntry);
            recordDataForAvgSecondsOnWeb.push({
              name: alignUTC(parse(m[0], 'yyyyMMdd', new Date()).toString()),
              value: Number(m[5]),
            } as ChartEntry);
            recordDataForTotalVisits.push({
              name: alignUTC(parse(m[0], 'yyyyMMdd', new Date()).toString()),
              value: Number(m[6]),
            } as ChartEntry);
          });
          const data = {
            totalVisitors: recordDataForTotalVisitors,
            pageViews: recordDataForPageViews,
            pagesPerSession: recordDataForPagesPerSession,
            bounceRate: recordDataForBounceRate,
            averageSecondsOnWebsite: recordDataForAvgSecondsOnWeb,
            totalVisits: recordDataForTotalVisits,
          } as GaRecordMap;
          observer.next(data);
          observer.complete();
        });
      });
    }
  }

  getTrafficSources(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
    defaultTracking: boolean,
    acessToken: string,
  ): Observable<GaSourceMap> {
    return this.getSourcesBaseQuery(
      partnerId,
      businessId,
      startDate,
      endDate,
      defaultTracking,
      'google_analytics_v2_traffic_sources',
      acessToken,
    );
  }

  getTopReferralSources(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
    defaultTracking: boolean,
    acessToken: string,
  ): Observable<GaSourceMap> {
    return this.getSourcesBaseQuery(
      partnerId,
      businessId,
      startDate,
      endDate,
      defaultTracking,
      'google_analytics_v2_top_referral_sources',
      acessToken,
    );
  }

  getGoals(partnerId: string, businessId: string, startDate: Date, endDate: Date): Observable<GaGoalMap> {
    const req = this.getGoalsRequest(partnerId, businessId, startDate, endDate);
    return this.multiLocationAnalyticsService.queryMetrics(req).pipe(
      filter((resp) => resp != null),
      map((resp: QueryMetricsResponse) => this.convertMetricsResponseToMap(resp)),
    );
  }

  getSourcesBaseQuery(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
    defaultTracking: boolean,
    metricName: string,
    accessToken: string,
  ): Observable<GaSourceMap> {
    if (defaultTracking === false) {
      const req: QueryMetricsRequest = this.getBaseQuery(metricName, partnerId, businessId);
      req.measures = [
        new Measure({
          aggregate: new Aggregate({
            measure: 'access_frequency',
            aggOp: Operator.SUM,
          }),
        }),
      ];
      req.groupBy = new GroupBy({
        dimension: [
          new Dimension({
            dimension: 'collection_timestamp',
          }),
          new Dimension({
            dimension: 'source',
          }),
        ],
      });
      req.alignmentPeriod = new AlignmentPeriod({
        calendar: Calendar.CALENDAR_YEAR,
      });

      req.alignment = Alignment.ALIGN_DELTA;
      req.dateRange = new DateRange({
        start: startDate,
        end: endDate,
      });
      return this.multiLocationAnalyticsService.queryMetrics(req).pipe(
        filter((resp) => resp != null),
        map((resp: QueryMetricsResponse) => {
          const metrics = resp.metricResults[0].metrics.metrics;
          if (!metrics) {
            return {};
          }
          return this.convertMetricsToMap(metrics);
        }),
      );
    } else {
      if (metricName === 'google_analytics_v2_traffic_sources') {
        return new Observable<GaSourceMap>((observer) => {
          gapi.analytics.ready(function (): void {
            gapi.analytics.auth.authorize({
              serverAuth: {
                access_token: accessToken,
              },
            });
          });

          this.lineDimension = this.pieGraphDefinition.dimension;
          this.lineMetrics = this.getLineMetrics(this.pieGraphDefinition);

          const respdata = this.buildQuery(
            this.getQueryOptions(this.lineMetrics, this.lineDimension, this.pieGraphDefinition, startDate, endDate),
          );
          respdata.subscribe((response) => {
            if (typeof response.rows === 'undefined') {
              observer.next({});
              observer.complete();
              return {};
            }
            observer.next(this.convertMetricsToMapDefault(response.rows));
            observer.complete();
          });
        });
      } else {
        return new Observable<GaSourceMap>((observer) => {
          gapi.analytics.ready(function (): void {
            gapi.analytics.auth.authorize({
              serverAuth: {
                access_token: accessToken,
              },
            });
          });
          this.lineDimension = this.barGraphDefinition.dimension;
          this.lineMetrics = this.getLineMetrics(this.barGraphDefinition);

          const respdata = this.buildQuery(
            this.getQueryOptions(this.lineMetrics, this.lineDimension, this.barGraphDefinition, startDate, endDate),
          );
          respdata.subscribe((response) => {
            if (typeof response.rows === 'undefined') {
              observer.next({});
              observer.complete();
              return {};
            }
            observer.next(this.convertMetricsToMapDefault(response.rows));
            observer.complete();
          });
        });
      }
    }
  }

  convertMetricsResponseToMap(resp: QueryMetricsResponse): GaGoalMap {
    const metrics = resp.metricResults[0].metrics.metrics;
    if (!metrics) {
      return {};
    }
    return this.convertGoalMetricsToMap(metrics);
  }

  convertMetricsToMap(metrics: MetricResult[]): GaSourceMap {
    const tableMap: GaSourceMap = {};
    metrics.forEach((m) => {
      const result = m.results.metrics[0];
      if (!tableMap[result.dimension]) {
        tableMap[result.dimension] = 0;
      }
      tableMap[result.dimension] += result.measures[0];
    });
    return tableMap;
  }

  convertMetricsToMapDefault(metrics: MetricResult[]): GaSourceMap {
    const tableMap: GaSourceMap = {};
    metrics.forEach((m) => {
      const result = m;
      tableMap[result[0]] = result[1];
    });
    return tableMap;
  }

  convertGoalMetricsToMap(metrics: MetricResult[]): GaGoalMap {
    const tableMap: GaGoalMap = {};
    metrics.forEach((m) => {
      const result = m.results.metrics[0];
      const goalId = result.dimension;
      const completions = result.results.metrics[0].measures[0];
      const goalName = result.results.metrics[0].dimension;
      if (!tableMap[goalId]) {
        tableMap[goalId] = {
          name: goalName,
          completions: completions,
        } as GaGoalData;
        return;
      }
      tableMap[goalId].completions += completions;
    });
    return tableMap;
  }

  private getBaseQuery(metricName: string, partnerId: string, businessId: string): QueryMetricsRequest {
    return new QueryMetricsRequest({
      partnerId: partnerId,
      metricName: metricName,
      resourceIds: [
        {
          businessId: { businessId: businessId },
        },
      ],
      filter: {
        compositeFilter: {
          op: CompositeFilter_Operator.AND,
          filters: [
            {
              fieldFilter: {
                dimension: 'category',
                operator: FieldFilter_Operator.EQUAL,
                value: {
                  valueType: Type.PROPERTY_TYPE_STRING,
                  value: Category[Category.CATEGORY_WEBSITE],
                },
              },
            },
            {
              fieldFilter: {
                dimension: 'subcategory',
                operator: FieldFilter_Operator.EQUAL,
                value: {
                  valueType: Type.PROPERTY_TYPE_STRING,
                  value: Category[Category.CATEGORY_WEBSITE],
                },
              },
            },
          ],
        },
      },
    });
  }

  private getGoalsRequest(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
  ): QueryMetricsRequest {
    const req: QueryMetricsRequest = this.getBaseQuery('google_analytics_v2_goals', partnerId, businessId);
    req.measures = [
      new Measure({
        aggregate: new Aggregate({
          measure: 'completions',
          aggOp: Operator.SUM,
        }),
      }),
    ];
    req.groupBy = new GroupBy({
      dimension: [
        new Dimension({
          dimension: 'collection_timestamp',
        }),
        new Dimension({
          dimension: 'goal_id',
        }),
        new Dimension({
          limitDimension: {
            dimension: 'goal_name',
            limit: 1,
            order: Order.ORDER_DESC,
          },
        }),
      ],
    });
    req.alignmentPeriod = new AlignmentPeriod({
      calendar: Calendar.CALENDAR_YEAR,
    });
    req.alignment = Alignment.ALIGN_DELTA;
    req.dateRange = new DateRange({
      start: startDate,
      end: endDate,
    });
    req.orderBy = new OrderBy({
      orderBy: [{ column: 'collection_date', order: Order.ORDER_DESC }],
    });
    return req;
  }

  /**Default Tracking Request queries */

  /**
   * Constructs and returns the query that is used for receiving data from Google Analytics
   * @param metrics - the specific metrics to query Google Analytics for
   * @param dimension - the dimension to query
   * @param graphDefinition - the graph definition that contains the filters associated with the query that is
   * constructed
   *
   * For more information on what each parameter of the query does, see https://developers.google.com/analytics/devguides/reporting/core/v3/reference
   */
  getQueryOptions(
    metrics: string,
    dimension: string,
    graphDefinition: AnalyticsGraphDefinition,
    start_date: Date,
    end_date: Date,
  ): any {
    const query: any = {
      ids: this.defaultViewId,
      'start-date': format(start_date, 'yyyy-MM-dd'),
      'end-date': format(end_date, 'yyyy-MM-dd'),
      metrics: metrics,
      dimensions: dimension,
      samplingLevel: 'HIGHER_PRECISION',
      quotaUser: this.siteId.substring(0, 40),
    };
    let gaFilter = `ga:dimension1==${this.siteId}`;
    if (graphDefinition.filters) {
      gaFilter += ';' + graphDefinition.filters;
    }
    query.filters = gaFilter;

    if (graphDefinition.sort) {
      query.sort = graphDefinition.sort;
    }

    /* tslint:disable:no-string-literal */
    if (graphDefinition.maxResults) {
      query['max-results'] = graphDefinition.maxResults;
    }
    /* tslint:enable:no-string-literal */
    return query;
  }

  /**
   * Authenticates with the legacy Google Analytics connection by getting authentication
   * data from the backend
   * @param siteId
   */
  authenticate(siteId: string): Observable<AnalyticsJSON> {
    const url = `/api/v1/analytics/${siteId}/authenticate`;
    return this.http.get(url).pipe(map((res: { data: any }) => res.data));
  }

  /**
   * Performs a reduce on the line metrics of a graph to return a final array that contains all the metrics, rather than
   * leave the graph definition with multiple line metrics
   * @param graphDefinition
   */
  getLineMetrics(graphDefinition: AnalyticsGraphDefinition): string {
    return graphDefinition.graphs
      .reduce((lineMetrics: string[], graph: AnalyticsGraph) => {
        lineMetrics = lineMetrics.concat(graph.metrics);
        return lineMetrics;
      }, [])
      .join(',');
  }

  /**
   * Constructs the query structure that is sent to Google Analytics for getting data about a specific GA view
   * @param params - return of getQueryOptions, which structured the query for the Google Analytics API
   */
  buildQuery(params: any): Observable<any> {
    const promise = new Promise((resolve, reject) => {
      const data = new gapi.analytics.report.Data({ query: params });
      data.once('success', resolve).once('error', reject).execute();
    });
    return from(promise);
  }
}
